import {
	Box,
	Typography,
	Divider,
	Select,
	MenuItem,
	TextField,
	Grid,
	useTheme,
} from '@mui/material';
import ResponsiveDialogue from '../../ResponsiveDialogue/ResponsiveDialogue';
import RoundedButton from '../../RoundedButton/RoundedButton';
import { useSelector } from 'react-redux';
import { selectAccount } from '../../../redux/slices/accountSlice.ts';
import { useGetAssetUserDefinedFieldsQuery } from '../../../api/asset.api.ts';
import React, { useMemo } from 'react';
import { ADD_UDF } from '../../../constants/messages.tsx';
import type { Asset } from '../../../api/models/asset.model.ts';

interface ModifyUserDefinedFieldsModalProps {
		open: boolean;
		onClose: () => void;
		selectedRows: Asset[];
		callUpdateUdfValues: (udfRecords: Record<string, string>) => void;
		downstreamCallInProgress: boolean;
}

export type UdfEditRow = {
	id: string;
	field: string | null;
	newValue: string | null;
}

const subHeader = 'Note: there may be a delay between updates being queued and being processed. You will see the job status once you submit the changes';

function ModifyUserDefinedFieldsModal({ open, onClose, selectedRows, callUpdateUdfValues, downstreamCallInProgress }: ModifyUserDefinedFieldsModalProps) {

	const theme = useTheme();

	const account = useSelector(selectAccount);
	const accountId = account?.accountId ?? 0;
	const { data: userDefinedFieldDefinitions } = useGetAssetUserDefinedFieldsQuery({ accountId });

	const initialUdfRows: UdfEditRow[] = [{
		id: crypto.randomUUID(),
		field: null,
		newValue: null
	}];

	const [udfRows, setUdfRows] = React.useState<UdfEditRow[]>(initialUdfRows);
	const [showAddUdf, setShowAddUdf] = React.useState(true);

	const cleanedUserDefinedFieldDefinitions = useMemo(() => {
		return Object.fromEntries(Object.entries(userDefinedFieldDefinitions ?? {}).filter(([_, value]) => value !== undefined && value !== null && value.trim() !== ''));
	}, [userDefinedFieldDefinitions]);

	const availableFieldsForRow = useMemo(() => {
		return (rowId: string) => {
			const selectedFields = new Set(
				udfRows
					.filter(row => row.id !== rowId && row.field !== null)
					.map(row => row.field as string)
			);

			const entries = Object.fromEntries(
				Object.entries(cleanedUserDefinedFieldDefinitions).filter(
					([key]) => !selectedFields.has(key)
				)
			);

			return entries;
		};
	}, [udfRows, cleanedUserDefinedFieldDefinitions]);

	React.useEffect(() => {
		if (udfRows.length >= Object.keys(cleanedUserDefinedFieldDefinitions).length) {
			setShowAddUdf(false);
		} else {
			setShowAddUdf(true);
		}
	}, [udfRows, cleanedUserDefinedFieldDefinitions]);

	const updateField = (id: string, field: string) => {
		setUdfRows(rows => rows.map(row => row.id === id ? { ...row, field } : row));
	}

	const updateValue = (id: string, newValue: string) => {
		setUdfRows(rows => rows.map(row => row.id === id ? { ...row, newValue } : row));
	}

	const addRow = () => {
		setUdfRows(rows => [...rows, {
			id: crypto.randomUUID(),
			field: null,
			newValue: null
		}]);
	}

	const columnStyling = {
		width: '48%'
	}

	const callToUpdateUdfValues = () => {
		const udfRecords = udfRows
			.filter(row => row.field !== null && row.newValue !== null)
			.reduce((acc, row) => {
				if (row.field && row.newValue) {
					acc[row.field] = row.newValue;
				}
				return acc;
			}, {} as Record<string, string>)

		callUpdateUdfValues(udfRecords)
	}

	const handleClosingModal = () => {
		setUdfRows(initialUdfRows);
		onClose();
	}

	return (
		<ResponsiveDialogue
			data-testid="edit-udf-dialogue"
			open={open}
			onClose={handleClosingModal}
			title="Edit"

		>
			<Box sx={{
				pt: 2
			}}>
				<Typography
					variant="body1"
					color={theme.palette.brand.primary}
					fontWeight={'bold'}
					paddingBottom={2}
				>
					{selectedRows.length} record{selectedRows.length !== 1 ? 's' : ''} currently selected.
				</Typography>
				<Typography variant="body1" fontWeight="bold">{subHeader}</Typography>
				<Grid container spacing={2} sx={{ mt: 2, mb: 1 }}>
					<Grid sx={columnStyling}>
						<Typography variant="h6">Field</Typography>
					</Grid>
					<Grid sx={columnStyling}>
						<Typography variant="h6">New Value</Typography>
					</Grid>
				</Grid>
				<Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
					<Grid sx={columnStyling}>
						<Divider sx={{ borderBottomWidth: 3 }} />
					</Grid>
					<Grid sx={columnStyling}>
						<Divider sx={{ borderBottomWidth: 3 }} />
					</Grid>
				</Grid>
				{udfRows.map((row, index) => (
					<Grid container spacing={2} alignItems="center" sx={{ mb: 2 }} key={row.id}>
						<Grid sx={columnStyling}>
							<Select
								data-testid={`udfFieldSelect-${index}`}
								value={row.field ?? ''}
								onChange={e => updateField(row.id, e.target.value)}
								fullWidth
							>
								{Object.entries(availableFieldsForRow(row.id))
									.filter(([_, value]) => value !== undefined)
									.map(([key, value]) => (
										<MenuItem key={key} value={key} data-testid={`udfField-${key}`}>
											{value}
										</MenuItem>
									))}
							</Select>
						</Grid>
						<Grid sx={columnStyling}>
							<TextField
								variant="outlined"
								value={row.newValue ?? ''}
								onChange={e => updateValue(row.id, e.target.value)}
								fullWidth
							/>
						</Grid>
					</Grid>
				))}
			</Box>
			{
				showAddUdf && (
					<Box
						sx={{
							color: (theme) => theme.palette.brand.primary,
							fontWeight: 'bold',
							fontSize: 13,
							cursor: 'pointer',
						}}
						onClick={() => {
							addRow();
						}}
					>
						{ADD_UDF}
					</Box>
				)
			}

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }}>
				<Box sx={{ pr: 2 }}>
					<RoundedButton
						variantStyle="light"
						variant="outlined"
						data-testid="cancel-button"
						onClick={handleClosingModal}
					>
						Cancel
					</RoundedButton>
				</Box>
				<Box>
					<RoundedButton
						variantStyle="dark"
						variant="contained"
						data-testid="save-and-apply-changes-button"
						disabled={downstreamCallInProgress}
						onClick={callToUpdateUdfValues}
					>
						Save and Apply Changes
					</RoundedButton>
				</Box>
			</Box>
		</ResponsiveDialogue>
	);
}

export default ModifyUserDefinedFieldsModal;